import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Twitter, Instagram, Facebook, Youtube } from 'lucide-react';
import axiosInstance from './axiosInstance';
import SiteLogo from './components/SiteLogo';
import { useSiteConfig } from './contexts/SiteConfigContext';

const LEGAL_LINKS = [
  { slug: 'terms-of-service', key: 'terms' },
  { slug: 'privacy-policy', key: 'privacy' },
  { slug: 'cookie-policy', key: 'cookies' },
];

// lucide-react ships no Telegram brand icon; site-config's "linkedin" field is repurposed for Telegram.
const TelegramIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M21.05 3.5 2.94 10.53c-1.24.5-1.23 1.19-.23 1.5l4.63 1.45 1.79 5.58c.22.6.37.84.76.84.34 0 .5-.16.7-.36l1.68-1.64 4.7 3.48c.87.48 1.49.23 1.71-.8L23.9 4.9c.32-1.26-.48-1.83-1.85-1.4Zm-3.13 3.4-8.53 7.71-.34 3.62-1.63-5.1 9.55-6.9c.44-.31.85-.14.52.17Z" />
  </svg>
);

const SOCIAL_PLATFORMS = [
  { key: 'facebook', Icon: Facebook },
  { key: 'twitter', Icon: Twitter },
  { key: 'instagram', Icon: Instagram },
  { key: 'linkedin', Icon: TelegramIcon },
  { key: 'youtube', Icon: Youtube },
];

const isSafeUrl = (url) => {
  try {
    return ['http:', 'https:'].includes(new URL(url, window.location.origin).protocol);
  } catch {
    return false;
  }
};

const Footer = () => {
  const { t } = useTranslation('common');
  const [pages, setPages] = useState([]);
  const { siteConfig } = useSiteConfig();
  const socialLinks = SOCIAL_PLATFORMS
    .map(({ key, Icon }) => ({ key, Icon, url: siteConfig.social_links?.[key] }))
    .filter(({ url }) => url && isSafeUrl(url));

  useEffect(() => {
    axiosInstance.get('/pages')
      .then(res => {
        const data = res.data?.data || res.data || [];
        setPages(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
  }, []);

  // Split pages into columns of ~4 each
  const cols = [];
  const perCol = Math.ceil(pages.length / 3) || 1;
  for (let i = 0; i < pages.length; i += perCol) {
    cols.push(pages.slice(i, i + perCol));
  }

  return (
    <footer className="w-full bg-white dark:bg-[#0a0a0a] py-8 border-t border-gray-100 dark:border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-4">
        <div className={`grid grid-cols-2 ${cols.length > 0 ? `md:grid-cols-${Math.min(cols.length + 1, 5)}` : 'md:grid-cols-2'} gap-8`}>
          {/* Brand column */}
          <div className="space-y-4">
            <Link to="/">
              <SiteLogo className="h-7" />
            </Link>
            <p className="text-gray-400 dark:text-gray-600 text-xs">© {new Date().getFullYear()}</p>
            {socialLinks.length > 0 && (
              <div className="flex gap-4">
                {socialLinks.map(({ key, Icon, url }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
                  >
                    <Icon size={18} />
                  </a>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {LEGAL_LINKS.map(({ slug, key }) => (
                <Link
                  key={slug}
                  to={`/page/${slug}`}
                  className="text-xs text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
                >
                  {t(`footer.${key}`)}
                </Link>
              ))}
            </div>
          </div>

          {/* Dynamic page columns */}
          {cols.map((col, i) => (
            <div key={i} className="space-y-3">
              {col.map(page => (
                <Link
                  key={page.id}
                  to={`/page/${page.slug}`}
                  className="block text-sm text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  {page.name}
                </Link>
              ))}
            </div>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
